/**
 * Field Registry helpers for Products Row Grouping DataGrid (Config C)
 *
 * Component-specific helpers and grouping constants. The field array itself
 * lives in the domain's merged registry: client/src/domains/products/fields.ts
 * (issue #585 Task 5 — supersedes the former flatFields.ts split).
 */

import { BaseFieldDefinition } from '../shared/types/fieldRegistry';
import { Product, FlatProduct } from '../services/api/domains/products/types';
import { getFieldsForSurface } from '../domains/products/fields';

/** Grid-surfaced fields, in registry (12-group) order. */
export const FLAT_PRODUCT_FIELDS = getFieldsForSurface('grid');

/**
 * Groupable dimensions - these fields can be used for row grouping
 */
export const GROUPABLE_FIELD_KEYS = [
  'preferred_product_name',
  'manufacturer_name',
  'facility_name',
  'facility_status',
  'grade_name',
  'therapeutic_area_name',
  'is_ndc_linked',
] as const;

/**
 * Get field definition by key
 */
export function getFieldByKey(
  key: keyof (Product & FlatProduct)
): BaseFieldDefinition<Product & FlatProduct> | undefined {
  return FLAT_PRODUCT_FIELDS.find(field => field.key === key);
}

/**
 * Get all groupable field definitions
 */
export function getGroupableFields(): BaseFieldDefinition<
  Product & FlatProduct
>[] {
  return FLAT_PRODUCT_FIELDS.filter(field =>
    GROUPABLE_FIELD_KEYS.includes(
      field.key as (typeof GROUPABLE_FIELD_KEYS)[number]
    )
  );
}

/**
 * Get default visible columns (initially all columns shown)
 */
export function getDefaultVisibleColumns(): string[] {
  return FLAT_PRODUCT_FIELDS.map(field => field.key as string);
}
